import { INSTALLATIONS } from "../game-data/catalog";
import type { CelestialBody, Development, FuelId, GameState, MaterialId } from "../shared/game-types";
import { addLog, clamp, enforceMaterialCapacity, FUEL_IDS, homeBodyId, incomingCargoReservation, MATERIAL_IDS, round } from "./core";
import { findBody } from "./generate-world";

const MATERIAL_BY_BODY: Record<CelestialBody["type"], readonly [MaterialId, MaterialId]> = {
  rocky: ["structural", "highTemp"],
  metallic: ["structural", "rare"],
  iceMoon: ["superconductor", "nuclear"],
  gasGiant: ["nuclear", "superconductor"],
  iceGiant: ["nuclear", "superconductor"],
  lava: ["highTemp", "rare"],
  comet: ["nuclear", "structural"],
  rogue: ["rare", "superconductor"],
};

type FuelYield = Record<Exclude<FuelId, "antimatter">, number>;

const FUEL_BY_BODY: Record<CelestialBody["type"], FuelYield> = {
  rocky: { chemicalFuel: 0.25, fissionFuel: 0.8, fusionFuel: 0.03, propellant: 0.2 },
  metallic: { chemicalFuel: 0.02, fissionFuel: 1, fusionFuel: 0, propellant: 0.02 },
  iceMoon: { chemicalFuel: 0.9, fissionFuel: 0.1, fusionFuel: 0.8, propellant: 1 },
  gasGiant: { chemicalFuel: 1.2, fissionFuel: 0, fusionFuel: 1.4, propellant: 1.2 },
  iceGiant: { chemicalFuel: 1, fissionFuel: 0, fusionFuel: 1.1, propellant: 1.4 },
  lava: { chemicalFuel: 0, fissionFuel: 0.85, fusionFuel: 0, propellant: 0 },
  comet: { chemicalFuel: 1.3, fissionFuel: 0.05, fusionFuel: 0.5, propellant: 1.5 },
  rogue: { chemicalFuel: 0.4, fissionFuel: 0.4, fusionFuel: 0.2, propellant: 0.5 },
};

const FUEL_REFINING_MULTIPLIER: FuelYield = { chemicalFuel: 6, fissionFuel: 2, fusionFuel: 1, propellant: 6 };

function emptyFuelRates(): Record<FuelId, number> {
  return { chemicalFuel: 0, fissionFuel: 0, fusionFuel: 0, antimatter: 0, propellant: 0 };
}

function energyFuel(level: number): { id: Exclude<FuelId, "propellant">; unitsPerEnergy: number } | undefined {
  if (level === 1) return { id: "chemicalFuel", unitsPerEnergy: 0.08 };
  if (level <= 5) return { id: "fissionFuel", unitsPerEnergy: 0.025 };
  if (level <= 8) return { id: "fusionFuel", unitsPerEnergy: 0.008 };
  if (level === 9) return { id: "antimatter", unitsPerEnergy: 0.0001 };
  return undefined;
}

function averageYield(body: CelestialBody, exploitationYears: number, deltaYears: number): number {
  const rate = Math.max(0, body.depletionRate);
  const high = Math.max(body.stableYield, body.baseYield);
  const stable = Math.max(0, body.stableYield);
  if (deltaYears <= 0) return stable + (high - stable) * Math.exp(-rate * exploitationYears);
  if (rate <= 1e-12) return high;
  const integral = (Math.exp(-rate * exploitationYears) - Math.exp(-rate * (exploitationYears + deltaYears))) / rate;
  return stable + (high - stable) * integral / deltaYears;
}

export interface EconomyRates {
  energyProduction: number;
  renewableEnergyProduction: number;
  fuelEnergyProduction: number;
  energyMaintenance: number;
  researchProduction: number;
  materialProduction: Partial<Record<MaterialId, number>>;
  fuelProduction: Record<FuelId, number>;
  fuelConsumption: Record<FuelId, number>;
}

export function calculateBodyEconomyRates(state: GameState, development: Development, deltaYears = 0): EconomyRates {
  const isHome = development.bodyId === homeBodyId(state);
  const renewableEnergyProduction = isHome ? 8 + state.player.levels.energy * 3 : 0;
  let fuelEnergyProduction = 0;
  let energyMaintenance = isHome ? 4 : 0;
  let researchProduction = isHome ? 4 + state.player.levels.industry : 0;
  const materialProduction: Partial<Record<MaterialId, number>> = {};
  const fuelProduction = emptyFuelRates();
  const body = findBody(state, development.bodyId);
  if (body) {
    const conditionFactor = clamp(development.condition / 100, 0.05, 1);
    const yieldValue = averageYield(body, development.exploitationYears, deltaYears) * conditionFactor;
    for (const [installation, countValue] of Object.entries(development.installations)) {
      const definition = INSTALLATIONS[installation as keyof typeof INSTALLATIONS];
      if (!definition) continue;
      const count = Math.max(0, countValue ?? 0);
      fuelEnergyProduction += definition.energyProduction * count * conditionFactor;
      energyMaintenance += definition.energyMaintenance * count * (1 + (body.environment - 1) * 0.11) * (1.2 - conditionFactor * 0.2);
      researchProduction += definition.researchProduction * count * conditionFactor;
      const produced = definition.materialProduction * count * (yieldValue / 30);
      if (produced > 0) {
        const [primary, secondary] = MATERIAL_BY_BODY[body.type];
        materialProduction[primary] = (materialProduction[primary] ?? 0) + produced * 0.78;
        materialProduction[secondary] = (materialProduction[secondary] ?? 0) + produced * 0.22;
      }
    }

    const mining = Math.max(0, development.installations.miningOutpost ?? 0);
    const collectors = Math.max(0, development.installations.atmosphericCollector ?? 0);
    const processors = Math.max(0, development.installations.orbitalProcessor ?? 0);
    const industry = Math.max(0, development.installations.industrialBase ?? 0) * 2 + Math.max(0, development.installations.colony ?? 0);
    const extraction = mining + collectors * 1.4;
    if (extraction > 0) {
      const refining = Math.min(3, 0.2 + processors * 0.55 + industry * 0.3);
      const source = FUEL_BY_BODY[body.type];
      const yieldScale = yieldValue / 30;
      for (const fuel of ["chemicalFuel", "fissionFuel", "fusionFuel", "propellant"] as const) {
        fuelProduction[fuel] += extraction * refining * yieldScale * source[fuel] * FUEL_REFINING_MULTIPLIER[fuel];
      }
    }
    if (state.player.levels.industry >= 9 && state.player.levels.energy >= 9 && processors + industry > 0) {
      fuelProduction.antimatter += (processors + industry) * conditionFactor * 0.0025 * (state.player.levels.industry - 8);
    }
    energyMaintenance += fuelProduction.antimatter * 80;
  }
  const fuelConsumption = emptyFuelRates();
  const activeFuel = energyFuel(state.player.levels.energy);
  if (activeFuel) fuelConsumption[activeFuel.id] = fuelEnergyProduction * activeFuel.unitsPerEnergy;
  return {
    energyProduction: renewableEnergyProduction + fuelEnergyProduction,
    renewableEnergyProduction,
    fuelEnergyProduction,
    energyMaintenance,
    researchProduction,
    materialProduction,
    fuelProduction,
    fuelConsumption,
  };
}

export function calculateEconomyRates(state: GameState, deltaYears = 0): EconomyRates {
  const aggregate: EconomyRates = {
    energyProduction: 0,
    renewableEnergyProduction: 0,
    fuelEnergyProduction: 0,
    energyMaintenance: 0,
    researchProduction: 0,
    materialProduction: {},
    fuelProduction: emptyFuelRates(),
    fuelConsumption: emptyFuelRates(),
  };
  for (const development of Object.values(state.developments)) {
    const rates = calculateBodyEconomyRates(state, development, deltaYears);
    aggregate.energyProduction += rates.energyProduction;
    aggregate.renewableEnergyProduction += rates.renewableEnergyProduction;
    aggregate.fuelEnergyProduction += rates.fuelEnergyProduction;
    aggregate.energyMaintenance += rates.energyMaintenance;
    aggregate.researchProduction += rates.researchProduction;
    for (const material of MATERIAL_IDS) aggregate.materialProduction[material] = (aggregate.materialProduction[material] ?? 0) + (rates.materialProduction[material] ?? 0);
    for (const fuel of FUEL_IDS) {
      aggregate.fuelProduction[fuel] += rates.fuelProduction[fuel];
      aggregate.fuelConsumption[fuel] += rates.fuelConsumption[fuel];
    }
  }
  return aggregate;
}

/** Aggregate stable production and maintenance over a segment; never loops by tick. */
export function advanceEconomy(state: GameState, deltaYears: number, messages: string[]): void {
  if (!(deltaYears > 0)) return;
  const bodyRates = Object.values(state.developments).map((development) => ({ development, rates: calculateBodyEconomyRates(state, development, deltaYears) }));
  const rates = calculateEconomyRates(state, deltaYears);
  const activeFuel = energyFuel(state.player.levels.energy);
  const desiredFuel = activeFuel ? rates.fuelConsumption[activeFuel.id] * deltaYears : 0;
  const evaluate = (fuelFactor: number) => {
    const fuelProduction = emptyFuelRates();
    const bodies = bodyRates.map(({ development, rates: localRates }) => {
      const stockpile = development.stockpile;
      const localEnergyProduction = localRates.renewableEnergyProduction + localRates.fuelEnergyProduction * fuelFactor;
      const requiredMaintenance = localRates.energyMaintenance * deltaYears;
      const grossEnergy = localEnergyProduction * deltaYears;
      const loadFactor = requiredMaintenance <= stockpile.energy + grossEnergy
        ? 1
        : clamp((stockpile.energy + grossEnergy) / Math.max(requiredMaintenance, 1e-9), 0, 1);
      const priorityFactor = (category: "research" | "industry" | "mining"): number => {
        const rank = state.automation.energyPriority.indexOf(category);
        return clamp(loadFactor * (1.34 - Math.max(0, rank) * 0.085), 0, 1);
      };
      const researchLoad = priorityFactor("research");
      const materialLoad = (priorityFactor("industry") + priorityFactor("mining")) / 2;
      for (const fuel of FUEL_IDS) fuelProduction[fuel] += localRates.fuelProduction[fuel] * materialLoad;
      return { development, localRates, localEnergyProduction, requiredMaintenance, grossEnergy, loadFactor, researchLoad, materialLoad };
    });
    return { bodies, fuelProduction };
  };

  let fuelFactor = desiredFuel > 0 && activeFuel
    ? clamp(state.resources.energy[activeFuel.id] / desiredFuel, 0, 1)
    : 1;
  let evaluation = evaluate(fuelFactor);
  if (activeFuel && desiredFuel > 0) {
    for (let iteration = 0; iteration < 8; iteration += 1) {
      const supported = clamp(
        (state.resources.energy[activeFuel.id] + evaluation.fuelProduction[activeFuel.id] * deltaYears) / desiredFuel,
        0,
        1,
      );
      if (Math.abs(supported - fuelFactor) <= 1e-9) break;
      fuelFactor = supported;
      evaluation = evaluate(fuelFactor);
    }
    const supported = clamp(
      (state.resources.energy[activeFuel.id] + evaluation.fuelProduction[activeFuel.id] * deltaYears) / desiredFuel,
      0,
      1,
    );
    if (supported < fuelFactor) {
      fuelFactor = supported;
      evaluation = evaluate(fuelFactor);
    }
  }
  const activeFuelConsumption = desiredFuel * fuelFactor;
  const fuelProduction = evaluation.fuelProduction;
  let actualEnergyProduction = 0;
  let actualResearchProduction = 0;

  for (const { development, localRates, localEnergyProduction, requiredMaintenance, grossEnergy, loadFactor, researchLoad, materialLoad } of evaluation.bodies) {
    const stockpile = development.stockpile;
    const reservation = incomingCargoReservation(state, development.bodyId);
    const effectiveEnergyCapacity = Math.max(stockpile.energy, stockpile.energyCapacity - (reservation.energy ?? 0));
    stockpile.energy = round(clamp(
      stockpile.energy + grossEnergy - requiredMaintenance * loadFactor,
      0,
      effectiveEnergyCapacity,
    ));
    const researchProduction = localRates.researchProduction * researchLoad;
    state.resources.research = round(Math.min(state.resources.researchCapacity, state.resources.research + researchProduction * deltaYears));
    actualResearchProduction += researchProduction;
    actualEnergyProduction += localEnergyProduction;
    const materialProduction = Object.entries(localRates.materialProduction) as Array<[MaterialId, number]>;
    const producedMaterials = materialProduction.reduce((sum, [, rate]) => sum + rate * materialLoad * deltaYears, 0);
    const storedMaterials = MATERIAL_IDS.reduce((sum, material) => sum + stockpile.materials[material], 0);
    const reservedMaterials = MATERIAL_IDS.reduce((sum, material) => sum + (reservation.materials?.[material] ?? 0), 0);
    const materialRoom = Math.max(0, stockpile.materialCapacity - storedMaterials - reservedMaterials);
    const storageFactor = producedMaterials > 0 ? Math.min(1, materialRoom / producedMaterials) : 1;
    for (const [material, rate] of materialProduction) {
      stockpile.materials[material] = round(stockpile.materials[material] + rate * materialLoad * storageFactor * deltaYears);
    }

    const body = findBody(state, development.bodyId);
    if (body) {
      const hasExtraction = (development.installations.miningOutpost ?? 0) > 0 || (development.installations.atmosphericCollector ?? 0) > 0;
      if (hasExtraction) development.exploitationYears = round(development.exploitationYears + deltaYears);
      const aging = deltaYears * (0.018 + body.environment * 0.006) * (loadFactor < 1 ? 1.5 : 1);
      development.condition = round(clamp(development.condition - aging, 20, 100));
      if (loadFactor < 0.999) {
        messages.push(`${body.name} 本地能源不足，自动降载至 ${Math.round(loadFactor * 100)}%`);
        addLog(state, "economy", "本地自动降载", `${body.name} 的非必要设施按 ${Math.round(loadFactor * 100)}% 负载结算。`, true);
      }
    }
  }

  state.resources.energy.productionPerYear = round(actualEnergyProduction);
  state.resources.energy.maintenancePerYear = round(rates.energyMaintenance);
  state.resources.researchPerYear = round(actualResearchProduction);
  for (const fuel of FUEL_IDS) {
    const production = fuelProduction[fuel];
    state.resources.energy[fuel] = round(state.resources.energy[fuel] + production * deltaYears);
    state.resources.energy.fuelProductionPerYear[fuel] = round(production);
    state.resources.energy.fuelConsumptionPerYear[fuel] = 0;
  }
  if (activeFuel) {
    const consumed = Math.min(activeFuelConsumption, state.resources.energy[activeFuel.id]);
    state.resources.energy[activeFuel.id] = round(state.resources.energy[activeFuel.id] - consumed);
    state.resources.energy.fuelConsumptionPerYear[activeFuel.id] = round(consumed / deltaYears);
  }
  enforceMaterialCapacity(state);
}
